import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCircleInfo, faPlus, faWallet } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import { formatShortDate } from '../../utils/financeWorkflow';

const buildEmptyForm = () => ({
  branch: '',
  vendorName: '',
  vendorServices: '',
  vendorEmail: '',
  vendorPhone: '',
  serviceDesc: '',
  commDate: '',
  paymentDetails: ''
});

function VendorsComp() {
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [detailsModalShow, setDetailsModalShow] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState('All');
  const [formState, setFormState] = useState(buildEmptyForm());

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);
        const [vendorSnapshot, branchSnapshot] = await Promise.all([
          getDocs(collection(db, 'Vendors')),
          getDocs(collection(db, 'Branches'))
        ]);

        const nextVendors = vendorSnapshot.docs
          .map((vendorDoc) => ({
            id: vendorDoc.id,
            ...vendorDoc.data()
          }))
          .sort((first, second) => {
            const firstTime = first.timeCreated?.seconds || 0;
            const secondTime = second.timeCreated?.seconds || 0;
            return secondTime - firstTime;
          });

        const nextBranches = branchSnapshot.docs.map((branchDoc) => ({
          id: branchDoc.id,
          ...branchDoc.data()
        }));

        setVendors(nextVendors);
        setBranches(nextBranches);
      } catch (error) {
        console.error('Failed to load vendors:', error);
        swal('Error', 'Vendors could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  useEffect(() => {
    if (branches.length === 0) {
      return;
    }

    setFormState((currentForm) => (
      currentForm.branch
        ? currentForm
        : {
            ...currentForm,
            branch: branches[0]?.id || 'General'
          }
    ));
  }, [branches]);

  const branchMap = useMemo(() => {
    return branches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});
  }, [branches]);

  const filteredVendors = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return vendors.filter((vendor) => {
      const branchName =
        vendor.branch === 'General'
          ? 'General'
          : branchMap[vendor.branch] || 'Campus not set';

      const matchesBranch =
        branchFilter === 'All' ||
        (branchFilter === 'General' && vendor.branch === 'General') ||
        vendor.branch === branchFilter;

      if (!matchesBranch) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        vendor.vendorName,
        vendor.vendorServices,
        vendor.vendorEmail,
        vendor.vendorPhone,
        vendor.serviceDesc,
        branchName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [branchFilter, branchMap, searchTerm, vendors]);

  const summary = useMemo(() => {
    const coveredBranches = new Set();
    const serviceLines = new Set();

    vendors.forEach((vendor) => {
      if (vendor.branch) {
        coveredBranches.add(vendor.branch);
      }

      if (vendor.vendorServices) {
        serviceLines.add(String(vendor.vendorServices).trim().toLowerCase());
      }
    });

    return {
      totalVendors: vendors.length,
      coveredCampuses: coveredBranches.size,
      serviceLines: serviceLines.size
    };
  }, [vendors]);

  const resetForm = () => {
    setFormState({
      ...buildEmptyForm(),
      branch: branches[0]?.id || 'General'
    });
  };

  const closeModal = () => {
    if (submitting) {
      return;
    }

    setModalShow(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const handleSubmitVendor = async () => {
    if (!formState.vendorName.trim() || !formState.vendorServices.trim()) {
      swal('Missing details', 'Vendor name and service line are required.', 'warning');
      return;
    }

    setSubmitting(true);

    try {
      const vendorRef = doc(collection(db, 'Vendors'));
      await setDoc(vendorRef, {
        branch: formState.branch || 'General',
        vendorName: formState.vendorName.trim(),
        vendorServices: formState.vendorServices.trim(),
        vendorEmail: formState.vendorEmail.trim(),
        vendorPhone: formState.vendorPhone.trim(),
        serviceDesc: formState.serviceDesc.trim(),
        paymentDetails: formState.paymentDetails.trim(),
        commDate: formState.commDate ? new Date(formState.commDate) : null,
        timeCreated: serverTimestamp(),
        docID: vendorRef.id
      });

      swal('Success', 'Vendor added successfully.', 'success');
      setModalShow(false);
      resetForm();

      const vendorSnapshot = await getDocs(collection(db, 'Vendors'));
      const nextVendors = vendorSnapshot.docs
        .map((vendorDoc) => ({
          id: vendorDoc.id,
          ...vendorDoc.data()
        }))
        .sort((first, second) => {
          const firstTime = first.timeCreated?.seconds || 0;
          const secondTime = second.timeCreated?.seconds || 0;
          return secondTime - firstTime;
        });
      setVendors(nextVendors);
    } catch (error) {
      console.error('Failed to add vendor:', error);
      swal('Error', 'Vendor could not be added right now.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="teamsComponent opsWorkspace">
      <section className="opsHero">
        <div className="opsHeroCopy">
          <span className="opsEyebrow">Finance workflow</span>
          <h3>Vendors</h3>
          <p>
            Keep your supplier directory clean, searchable, and ready for fixed bills and operations
            payments.
          </p>
        </div>
        <button type="button" className="opsPrimaryBtn" onClick={() => setModalShow(true)}>
          <FontAwesomeIcon icon={faPlus} /> Add vendor
        </button>
      </section>

      <section className="opsStatsGrid">
        <article className="opsStatCard">
          <span>Total vendors</span>
          <strong>{summary.totalVendors}</strong>
        </article>
        <article className="opsStatCard">
          <span>Campuses covered</span>
          <strong>{summary.coveredCampuses}</strong>
        </article>
        <article className="opsStatCard">
          <span>Service lines</span>
          <strong>{summary.serviceLines}</strong>
        </article>
      </section>

      <section className="opsPanel">
        <div className="opsToolbar">
          <div>
            <h4>Supplier directory</h4>
            <p>These vendor records feed the fixed bills and payment pages.</p>
          </div>
          <div className="opsFilters">
            <Form.Control
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search vendor, service, phone..."
            />
            <Form.Select value={branchFilter} onChange={(event) => setBranchFilter(event.target.value)}>
              <option value="All">All campuses</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </option>
              ))}
              <option value="General">General</option>
            </Form.Select>
          </div>
        </div>

        {loading ? (
          <div className="opsLoadingCard">
            <Spinner animation="border" role="status" />
            <span>Loading vendors...</span>
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="opsEmptyState">
            <h6>No vendors found</h6>
            <p>Try a different search term or add the first vendor for this workflow.</p>
          </div>
        ) : (
          <div className="opsTableWrap">
            <table className="opsTable">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Campus</th>
                  <th>Service line</th>
                  <th>Contact</th>
                  <th>Started</th>
                  <th>Payment details</th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((vendor) => {
                  const branchName =
                    vendor.branch === 'General'
                      ? 'General'
                      : branchMap[vendor.branch] || 'Campus not set';

                  return (
                    <tr key={vendor.id}>
                      <td data-label="Vendor">
                        <div className="opsIdentityCell">
                          <span className="opsIdentityIcon">
                            <FontAwesomeIcon icon={faBuilding} />
                          </span>
                          <div>
                            <strong>{vendor.vendorName || 'Vendor'}</strong>
                            <small>{vendor.serviceDesc || 'No description yet'}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Campus">{branchName}</td>
                      <td data-label="Service line">{vendor.vendorServices || '—'}</td>
                      <td data-label="Contact">
                        <div className="opsDetailStack">
                          <strong>{vendor.vendorPhone || 'Phone not set'}</strong>
                          <small>{vendor.vendorEmail || 'Email not set'}</small>
                        </div>
                      </td>
                      <td data-label="Started">{formatShortDate(vendor.commDate, 'Not set')}</td>
                      <td data-label="Payment details">
                        <button
                          type="button"
                          className="opsGhostBtn"
                          onClick={() => {
                            setSelectedVendor(vendor);
                            setDetailsModalShow(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faWallet} /> View details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal show={modalShow} onHide={closeModal} centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Add vendor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Campus</Form.Label>
            <Form.Select name="branch" value={formState.branch} onChange={handleFormChange}>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </option>
              ))}
              <option value="General">General</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Vendor name</Form.Label>
            <Form.Control
              name="vendorName"
              value={formState.vendorName}
              onChange={handleFormChange}
              placeholder="e.g. Landlord, Water supplier"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Service line</Form.Label>
            <Form.Control
              name="vendorServices"
              value={formState.vendorServices}
              onChange={handleFormChange}
              placeholder="e.g. Rent, Internet, Cleaning"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              name="vendorEmail"
              value={formState.vendorEmail}
              onChange={handleFormChange}
              placeholder="accounts@vendor.com"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              name="vendorPhone"
              value={formState.vendorPhone}
              onChange={handleFormChange}
              placeholder="07..."
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Start date</Form.Label>
            <Form.Control
              type="datetime-local"
              name="commDate"
              value={formState.commDate}
              onChange={handleFormChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Service description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="serviceDesc"
              value={formState.serviceDesc}
              onChange={handleFormChange}
              placeholder="What they provide and any notes the team should know."
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Payment details</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="paymentDetails"
              value={formState.paymentDetails}
              onChange={handleFormChange}
              placeholder="Bank details, MPESA paybill, account number..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={closeModal} disabled={submitting}>
            Close
          </Button>
          <Button variant="dark" onClick={handleSubmitVendor} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              'Save vendor'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={detailsModalShow} onHide={() => setDetailsModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Vendor details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVendor ? (
            <div className="opsModalPreview">
              <div className="opsModalPreviewRow">
                <span>
                  <FontAwesomeIcon icon={faCircleInfo} /> Vendor
                </span>
                <strong>{selectedVendor.vendorName || 'Vendor'}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Campus</span>
                <strong>
                  {selectedVendor.branch === 'General'
                    ? 'General'
                    : branchMap[selectedVendor.branch] || 'Campus not set'}
                </strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Service line</span>
                <strong>{selectedVendor.vendorServices || '—'}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Contact</span>
                <strong>{selectedVendor.vendorPhone || selectedVendor.vendorEmail || 'Not set'}</strong>
              </div>
              <div className="opsModalCopyBlock">
                <span>Service notes</span>
                <p>{selectedVendor.serviceDesc || 'No extra service notes yet.'}</p>
              </div>
              <div className="opsModalCopyBlock">
                <span>Payment details</span>
                <p>{selectedVendor.paymentDetails || 'No payment details added yet.'}</p>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={() => setDetailsModalShow(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default VendorsComp;
